import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { OpenInNewIcon } from '@change/ds-icons';

import { Translate } from '@change-corgi/core/react/i18n';

import {
	avatarPlaceholder,
	container,
	divider,
	endorsementText,
	endorsementTextContainer,
	endorserDetails,
	endorserInfo,
	endorserTitleRow,
} from './EndorsementPreview.styles';

type EndorsementPreviewProps = {
	name?: string;
	title?: string;
	statement?: string;
	photoUrl?: string;
	url?: string;
};

export const EndorsementPreview: React.FC<EndorsementPreviewProps> = ({ name, title, statement, photoUrl, url }) => {
	const displayName = name || undefined;
	const displayTitle = title || undefined;
	const displayStatement = statement || undefined;

	return (
		<div className="flex flex-col gap-5">
			<div className={container()} data-testid="endorsement-preview">
				<div className={endorserInfo()}>
					<div className={avatarPlaceholder()}>
						{photoUrl ? (
							<img
								src={photoUrl}
								alt={displayName || ''}
								className="h-[54px] w-[54px] rounded-full object-cover"
								data-testid="preview-photo"
							/>
						) : (
							<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 66 66">
								<path
									stroke="#fff"
									strokeWidth="7"
									d="M28.061 4.977a8.77 8.77 0 0 1 9.743 0c.39.26.867.356 1.327.264a8.77 8.77 0 0 1 9 3.729c.26.39.665.66 1.125.75a8.769 8.769 0 0 1 6.817 6.565l.072.324.041.17c.119.39.369.727.71.955a8.77 8.77 0 0 1 3.728 9c-.091.46.003.938.264 1.327a8.77 8.77 0 0 1 0 9.743 1.77 1.77 0 0 0-.264 1.327 8.77 8.77 0 0 1-3.729 9c-.39.26-.659.665-.75 1.125a8.77 8.77 0 0 1-6.89 6.889c-.459.091-.864.36-1.124.75a8.77 8.77 0 0 1-9 3.729 1.77 1.77 0 0 0-1.327.264 8.77 8.77 0 0 1-9.743 0 1.769 1.769 0 0 0-1.327-.264 8.769 8.769 0 0 1-9-3.729 1.761 1.761 0 0 0-1.125-.75 8.77 8.77 0 0 1-6.888-6.89 1.768 1.768 0 0 0-.751-1.124 8.77 8.77 0 0 1-3.729-9 1.77 1.77 0 0 0-.263-1.327 8.77 8.77 0 0 1 0-9.743c.26-.39.355-.867.263-1.327a8.77 8.77 0 0 1 3.729-9c.39-.26.66-.665.75-1.125a8.77 8.77 0 0 1 6.89-6.888c.46-.092.864-.362 1.124-.751a8.77 8.77 0 0 1 9-3.729c.46.092.938-.003 1.327-.264Z"
								/>
								<path
									stroke="#E1DDE9"
									strokeWidth="5"
									d="M28.617 5.809a7.769 7.769 0 0 1 8.631 0 2.77 2.77 0 0 0 2.077.413A7.77 7.77 0 0 1 47.3 9.525a2.77 2.77 0 0 0 1.761 1.177 7.768 7.768 0 0 1 6.103 6.103 2.77 2.77 0 0 0 1.177 1.761 7.77 7.77 0 0 1 3.304 7.974c-.143.72.005 1.467.413 2.077a7.769 7.769 0 0 1 0 8.631 2.767 2.767 0 0 0-.413 2.077A7.77 7.77 0 0 1 56.34 47.3a2.77 2.77 0 0 0-1.177 1.761 7.768 7.768 0 0 1-6.102 6.103 2.77 2.77 0 0 0-1.762 1.177 7.77 7.77 0 0 1-7.974 3.303 2.768 2.768 0 0 0-2.077.414 7.77 7.77 0 0 1-8.63 0 2.768 2.768 0 0 0-2.078-.413 7.77 7.77 0 0 1-7.974-3.304 2.77 2.77 0 0 0-1.761-1.177 7.768 7.768 0 0 1-6.103-6.102A2.77 2.77 0 0 0 9.525 47.3a7.77 7.77 0 0 1-3.303-7.974 2.768 2.768 0 0 0-.413-2.077 7.77 7.77 0 0 1 0-8.63 2.774 2.774 0 0 0 .413-2.078 7.77 7.77 0 0 1 3.303-7.974 2.769 2.769 0 0 0 1.177-1.761 7.768 7.768 0 0 1 6.103-6.103 2.77 2.77 0 0 0 1.761-1.177 7.77 7.77 0 0 1 7.974-3.303 2.768 2.768 0 0 0 2.077-.413Z"
								/>
								<path
									fill="#403A49"
									fillOpacity=".1"
									stroke="#766F81"
									strokeWidth="2"
									d="M29.45 7.056a6.27 6.27 0 0 1 6.965 0 4.268 4.268 0 0 0 3.203.637 6.268 6.268 0 0 1 6.434 2.665 4.27 4.27 0 0 0 2.716 1.815 6.27 6.27 0 0 1 4.924 4.925 4.27 4.27 0 0 0 1.815 2.715 6.268 6.268 0 0 1 2.665 6.434 4.268 4.268 0 0 0 .638 3.203 6.27 6.27 0 0 1 0 6.965 4.268 4.268 0 0 0-.638 3.203 6.268 6.268 0 0 1-2.665 6.434 4.27 4.27 0 0 0-1.815 2.715 6.27 6.27 0 0 1-4.924 4.925 4.27 4.27 0 0 0-2.716 1.815 6.268 6.268 0 0 1-6.434 2.665 4.268 4.268 0 0 0-3.203.638 6.27 6.27 0 0 1-6.965 0 4.268 4.268 0 0 0-3.203-.638 6.268 6.268 0 0 1-6.433-2.665 4.27 4.27 0 0 0-2.716-1.815 6.27 6.27 0 0 1-4.925-4.925 4.27 4.27 0 0 0-1.815-2.715 6.268 6.268 0 0 1-2.665-6.434 4.268 4.268 0 0 0-.637-3.203 6.27 6.27 0 0 1 0-6.965 4.268 4.268 0 0 0 .637-3.203 6.268 6.268 0 0 1 2.665-6.434 4.27 4.27 0 0 0 1.815-2.715 6.27 6.27 0 0 1 4.925-4.925 4.27 4.27 0 0 0 2.716-1.815 6.268 6.268 0 0 1 6.433-2.665 4.268 4.268 0 0 0 3.203-.637Z"
								/>
								<path
									fill="#766F81"
									d="M39.933 25.933v14h-14v-14h14Zm0-2h-14c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-14c0-1.1-.9-2-2-2Zm-4.86 8.86-3 3.87-2.14-2.59-3 3.86h12l-3.86-5.14Z"
								/>
							</svg>
						)}
					</div>

					<div className={endorserDetails()}>
						<div className={endorserTitleRow()}>
							<Text
								variant="body"
								size="large"
								fontWeight="bold"
								className="text-typography-weak"
								data-testid="preview-name"
							>
								{displayName || (
									<Translate value="corgi.petition.starter_dashboard_v2.endorsements.add.preview_name_placeholder" />
								)}
							</Text>
							{url && <OpenInNewIcon className="text-typography-weak" size="small" data-testid="preview-url-icon" />}
						</div>
						<Text variant="body" size="small" className="text-typography-disabled" data-testid="preview-title">
							{displayTitle || (
								<Translate value="corgi.petition.starter_dashboard_v2.endorsements.add.preview_title_placeholder" />
							)}
						</Text>
					</div>
				</div>

				<div className={divider()} />

				<div className={endorsementTextContainer()}>
					<Text className={endorsementText()} data-testid="preview-statement">
						{displayStatement || (
							<Translate value="corgi.petition.starter_dashboard_v2.endorsements.add.preview_statement_placeholder" />
						)}
					</Text>
				</div>
			</div>
		</div>
	);
};
